/**
 *
 * Merchant Location Card
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  FormattedMessage,
  useFormatMessage,
} from '@artemis/integrations/contentful/utils';
import ResponsiveImage from '@artemis/components/ResponsiveImage';
import MerchantStateOverlay from './MerchantStateOverlay';
import MerchantStateLabel from './MerchantStateLabel';
import { getMerchantState } from './utils';

const Container = styled.a`
  color: ${({ theme }) => theme.palette.common.black};
  text-decoration: none;
  display: flex;
`;

const ImageContainer = styled.div`
  display: flex;
  height: fit-content;
  position: relative;
  margin-right: 16px;
  ${props => props.theme.isTablet`
    margin-right: 40px;
  `}
`;

const Image = styled.img`
  border-radius: 12px;
  width: 90px;
  height: 90px;
  object-fit: cover;
  ${props => props.theme.isTablet`
    width: 130px;
    height: 130px;
  `}
`;

const Details = styled.div`
  flex: 1;
`;

const Heading = styled.div`
  display: block;
  margin-bottom: 4px;
  ${props => props.theme.isTablet`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  `}
`;

const Name = styled.h6`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const Distance = styled.p`
  ${({ theme }) => theme.typography.bodyLarge};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const Body = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const StoreInfo = styled.div`
  > *:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const TodaysHours = styled.p`
  color: ${({ theme, disabled }) =>
    disabled ? theme.palette.text.regular.disabled : theme.palette.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
`;

const Address = styled.p`
  ${({ theme }) => theme.typography.bodySmall};
`;

const Options = styled.div`
  display: flex;
  > :not(:last-child) {
    margin-right: 18px;
  }
`;

const Option = styled.p`
  ${({ theme }) => theme.typography.bodySmall};
  display: flex;
  align-items: center;
`;

const Icon = styled(ResponsiveImage)`
  width: 20px;
  height: 20px;
  margin-right: 6px;
`;

const Button = styled.div`
  color: ${({ theme }) => theme.palette.common.white};
  ${({ theme }) => theme.typography.button};
  padding: 10px 30px;
  background: ${({ theme }) => theme.palette.primary};
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  display: none;
  ${props => props.theme.isTablet`
    display: block;
  `}
`;

const MerchantLocationCard = ({
  name,
  squareImageUrl = null,
  imageUrl = null,
  address: { streetAddress, city },
  todaysHours,
  supportsDelivery,
  supportsPickup,
  className,
  open,
  url,
  state,
  distance,
}) => {
  const merchantState = getMerchantState({ state, open });
  const defaultImageUrl = useFormatMessage({
    entry: 'brand.location.defaultImageUrl',
  });
  return (
    <Container className={className} href={url}>
      <ImageContainer>
        <Image
          src={squareImageUrl || imageUrl || defaultImageUrl}
          width="90"
          height="90"
          alt={name}
        />
        <MerchantStateOverlay label={merchantState} />
      </ImageContainer>
      <Details>
        <Heading>
          <Name>{name}</Name>
          {distance?.value && (
            <Distance data-testid="merchant-location-card-distance">
              {distance.value.toFixed(1)}
              {distance.unit}
            </Distance>
          )}
        </Heading>
        <Body>
          <StoreInfo>
            <TodaysHours disabled={merchantState}>
              <MerchantStateLabel
                label={merchantState}
                todaysHours={todaysHours}
              />
            </TodaysHours>
            <Address>
              {streetAddress}
              {city ? `, ${city}` : ''}
            </Address>
            <Options>
              {supportsDelivery && (
                <Option data-testid="merchant-location-card-delivery">
                  <Icon id="icons.deliveryBlack.img" />
                  <FormattedMessage entry="menu.delivery" />
                </Option>
              )}
              {supportsPickup && (
                <Option data-testid="merchant-location-card-pickup">
                  <Icon id="icons.pickupBlack.img" />
                  <FormattedMessage entry="menu.pickup" />
                </Option>
              )}
            </Options>
          </StoreInfo>
          <Button>
            <FormattedMessage entry="brand.viewMenu" />
          </Button>
        </Body>
      </Details>
    </Container>
  );
};

MerchantLocationCard.propTypes = {
  name: PropTypes.string.isRequired,
  squareImageUrl: PropTypes.string,
  address: PropTypes.shape({
    streetAddress: PropTypes.string.isRequired,
    city: PropTypes.string,
  }).isRequired,
  imageUrl: PropTypes.string,
  supportsDelivery: PropTypes.bool.isRequired,
  supportsPickup: PropTypes.bool,
  className: PropTypes.string,
  url: PropTypes.string,
  open: PropTypes.bool,
  todaysHours: PropTypes.string,
  state: PropTypes.string,
  distance: PropTypes.shape({
    value: PropTypes.number,
    unit: PropTypes.string,
  }),
};

MerchantLocationCard.defaultProps = {};

export default MerchantLocationCard;
